import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SafeImage } from '@/components/ui/safe-image';
import { Images, Loader2, MapPinPlus, PlusSquare, Video } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { sanitizeFloorplanWritePayload } from '../lib/floorplanZoneUtils';
import type { AisleDefinition, StoreProfileDetail, UnmappedEvidenceItem } from '../types';

interface UnmappedEvidenceInboxProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
  /** Called after an item is successfully assigned (e.g. to select the aisle) */
  onAssigned?: (aisleId: string | null, zoneId: string | null) => void;
}

const KIND_LABELS: Record<UnmappedEvidenceItem['kind'], string> = {
  walkthrough: 'Walk-through',
  reference_image: 'Store image',
  gallery_image: 'Campaign photo',
};

function nextAislePosition(index: number) {
  const col = index % 6;
  const row = Math.floor(index / 6);
  return { x: 8 + col * 14, y: 18 + row * 10, width: 10, height: 8 };
}

/**
 * Inbox for photos and videos that still require spatial assignment.
 * Every assignment links evidence to an EXISTING canonical aisle/zone — the
 * only way a new aisle is created is the deliberate "Create Aisle N and attach"
 * action, so no duplicate spatial entities are ever produced silently.
 */
export function UnmappedEvidenceInbox({ profile, storeId, storeIdParam, onAssigned }: UnmappedEvidenceInboxProps) {
  const [pendingItemId, setPendingItemId] = useState<string | null>(null);
  const [assignTargets, setAssignTargets] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canEdit = storeId != null && storeIdParam != null;

  const items = [...profile.pendingSuggestions, ...profile.unmappedEvidence];

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles/summary'] }),
    ]);
  };

  const fail = (title: string, error: unknown) => {
    toast({
      title,
      description: error instanceof Error ? error.message : 'Unknown error',
      variant: 'destructive',
    });
  };

  const assignWalkthroughToAisle = async (item: UnmappedEvidenceItem, aisleId: string) => {
    if (storeId == null || item.liveId == null) return;
    setPendingItemId(item.id);
    try {
      const res = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${item.liveId}`,
        'PATCH',
        { floorplanAisleIds: [aisleId] },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to assign walkthrough');
      }
      await invalidate();
      toast({ title: 'Walk-through assigned', description: 'Linked to the canonical aisle.' });
      onAssigned?.(aisleId, null);
    } catch (error) {
      fail('Could not assign walk-through', error);
    } finally {
      setPendingItemId(null);
    }
  };

  const assignReferenceImage = async (item: UnmappedEvidenceItem, target: { kind: 'aisle' | 'zone'; id: string }) => {
    if (storeId == null || item.liveId == null) return;
    setPendingItemId(item.id);
    try {
      const res = await apiRequest(
        `/api/store-profiles/${storeId}/reference-images/${item.liveId}`,
        'PATCH',
        target.kind === 'aisle'
          ? { floorplanAisleId: target.id }
          : { floorplanZoneId: target.id },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to assign image');
      }
      await invalidate();
      toast({ title: 'Image assigned' });
      onAssigned?.(target.kind === 'aisle' ? target.id : null, target.kind === 'zone' ? target.id : null);
    } catch (error) {
      fail('Could not assign image', error);
    } finally {
      setPendingItemId(null);
    }
  };

  const assignGalleryImageToZone = async (item: UnmappedEvidenceItem, zoneId: string) => {
    if (storeId == null) return;
    setPendingItemId(item.id);
    try {
      const res = await apiRequest(
        `/api/store-profiles/${storeId}/floorplan/image-tags`,
        'PATCH',
        { imageId: item.id, zoneId },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to assign photo');
      }
      await invalidate();
      toast({ title: 'Photo assigned to zone' });
      onAssigned?.(null, zoneId);
    } catch (error) {
      fail('Could not assign photo', error);
    } finally {
      setPendingItemId(null);
    }
  };

  /**
   * Deliberate creation of ONE canonical aisle, then the walkthrough is attached
   * to it. The floorplan PUT replaces the full aisle list, so we append to the
   * existing list rather than fabricating a parallel aisle model.
   */
  const createAisleAndAttach = async (item: UnmappedEvidenceItem) => {
    if (storeId == null || item.liveId == null || item.suggestedAisleNumber == null) return;
    setPendingItemId(item.id);
    try {
      const position = nextAislePosition(profile.aisles.length);
      const newAisle = {
        id: `aisle-${Date.now()}`,
        number: item.suggestedAisleNumber,
        name: item.suggestedLocationLabel ?? `Aisle ${item.suggestedAisleNumber}`,
        category: 'General',
        lengthMetres: 0,
        ...position,
      };
      const existingAisles = profile.aisles.map(({ dataSource: _dataSource, ...aisle }) => aisle);
      const payload = sanitizeFloorplanWritePayload({
        aisles: [...existingAisles, newAisle] as unknown as Array<Record<string, unknown>>,
        zones: profile.floorplanZones as unknown as Array<Record<string, unknown>>,
      });
      const putRes = await apiRequest(
        `/api/store-profiles/${storeId}/floorplan`,
        'PUT',
        payload,
      );
      if (!putRes.ok) {
        const body = await putRes.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to create aisle');
      }

      const patchRes = await apiRequest(
        `/api/store-profiles/${storeId}/walkthroughs/${item.liveId}`,
        'PATCH',
        { floorplanAisleIds: [newAisle.id] },
      );
      if (!patchRes.ok) {
        const body = await patchRes.json().catch(() => ({}));
        throw new Error(body.error ?? 'Aisle created but attaching the walk-through failed');
      }

      await invalidate();
      toast({
        title: `Aisle ${item.suggestedAisleNumber} created`,
        description: 'The walk-through is now attached to it.',
      });
      onAssigned?.(newAisle.id, null);
    } catch (error) {
      fail('Could not create aisle', error);
    } finally {
      setPendingItemId(null);
    }
  };

  const assignOptionsFor = (item: UnmappedEvidenceItem) => {
    if (item.kind === 'walkthrough') {
      return profile.aisles.map((aisle) => ({ value: `aisle:${aisle.id}`, label: `Aisle ${aisle.number} — ${aisle.name}` }));
    }
    if (item.kind === 'reference_image') {
      return [
        ...profile.aisles.map((aisle) => ({ value: `aisle:${aisle.id}`, label: `Aisle ${aisle.number} — ${aisle.name}` })),
        ...profile.floorplanZones.map((zone) => ({ value: `zone:${zone.id}`, label: `Zone: ${zone.label}` })),
      ];
    }
    return profile.floorplanZones.map((zone) => ({ value: `zone:${zone.id}`, label: `Zone: ${zone.label}` }));
  };

  const runAssign = (item: UnmappedEvidenceItem, targetValue: string) => {
    const [kind, ...rest] = targetValue.split(':');
    const id = rest.join(':');
    if (!id) return;
    if (item.kind === 'walkthrough') {
      void assignWalkthroughToAisle(item, id);
    } else if (item.kind === 'reference_image') {
      void assignReferenceImage(item, { kind: kind === 'aisle' ? 'aisle' : 'zone', id });
    } else {
      void assignGalleryImageToZone(item, id);
    }
  };

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-gray-500">
          <MapPinPlus className="h-8 w-8 mx-auto mb-2 text-emerald-500" aria-hidden />
          All evidence has a spatial home. Nothing waiting for assignment.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">
        {items.length} item{items.length === 1 ? '' : 's'} need{items.length === 1 ? 's' : ''} a canonical aisle or zone.
        Suggestions come from posting-instruction locations — confirm or override them.
      </p>
      {items.map((item) => {
        const options = assignOptionsFor(item);
        const hasSuggestion = item.suggestedAisleNumber != null
          && !profile.aisles.some((aisle: AisleDefinition) => aisle.number === item.suggestedAisleNumber);
        const selectedTarget = assignTargets[item.id] ?? '';
        return (
          <Card key={item.id}>
            <CardContent className="p-3 flex flex-col sm:flex-row gap-3">
              <div className="w-20 h-14 rounded-md overflow-hidden bg-slate-100 shrink-0">
                {item.thumbnailUrl ? (
                  <SafeImage
                    src={item.thumbnailUrl}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    placeholderType="photo"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {item.kind === 'walkthrough'
                      ? <Video className="h-5 w-5 text-slate-400" aria-hidden />
                      : <Images className="h-5 w-5 text-slate-400" aria-hidden />}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium truncate">{item.title}</p>
                  <Badge variant="outline" className="text-[10px]">{KIND_LABELS[item.kind]}</Badge>
                </div>
                <p className="text-xs text-gray-500">{item.captureDate || 'No date'}</p>
                {item.suggestedLocationLabel && (
                  <p className="text-xs text-sky-700 mt-0.5">
                    Suggested from posting location: {item.suggestedLocationLabel}
                  </p>
                )}
              </div>
              {canEdit && (
                <div className="flex flex-col gap-2 sm:w-64 shrink-0">
                  {options.length > 0 ? (
                    <div className="flex gap-2">
                      <Select
                        value={selectedTarget}
                        onValueChange={(value) => setAssignTargets((current) => ({ ...current, [item.id]: value }))}
                      >
                        <SelectTrigger aria-label={`Assign ${item.title} to an aisle or zone`}>
                          <SelectValue placeholder="Assign to…" />
                        </SelectTrigger>
                        <SelectContent>
                          {options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        size="sm"
                        disabled={!selectedTarget || pendingItemId === item.id}
                        onClick={() => runAssign(item, selectedTarget)}
                      >
                        {pendingItemId === item.id ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : 'Assign'}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">Define an aisle or zone first to assign this item.</p>
                  )}
                  {item.kind === 'walkthrough' && hasSuggestion && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={pendingItemId === item.id}
                      onClick={() => void createAisleAndAttach(item)}
                    >
                      {pendingItemId === item.id ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
                      ) : (
                        <PlusSquare className="h-4 w-4 mr-1" aria-hidden />
                      )}
                      Create Aisle {item.suggestedAisleNumber} and attach
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
